// Typed client for /api/engage (portal side). Contract: @gf/shared/features/engage.
import type { ID, Page } from "@gf/shared";
import type {
  EngageCampaign,
  EngageCampaignCreateReq,
  EngageCampaignUpdateReq,
  EngageDeleteRes,
  EngageDeleteThen,
  EngageOverview,
  EngageStats,
  EngageTarget,
  EngageTargetStatus,
} from "@gf/shared/features/engage";
import { api } from "../../lib/api";

const enc = encodeURIComponent;

export const engageApi = {
  list: (igAccountId: ID) => api<{ items: EngageCampaign[] }>("/api/engage/campaigns", { query: { igAccountId } }),
  get: (id: ID) => api<EngageCampaign>(`/api/engage/campaigns/${enc(id)}`),
  create: (body: EngageCampaignCreateReq) => api<EngageCampaign>("/api/engage/campaigns", { method: "POST", body }),
  update: (id: ID, body: EngageCampaignUpdateReq) => api<EngageCampaign>(`/api/engage/campaigns/${enc(id)}`, { method: "PATCH", body }),
  /** keep: gone now, follows stay. unfollow: hidden now, its pending auto-unfollows drain, then it's purged. */
  remove: (id: ID, then: EngageDeleteThen = "keep") => api<EngageDeleteRes>(`/api/engage/campaigns/${enc(id)}`, { method: "DELETE", query: { then } }),
  setStatus: (id: ID, status: "active" | "paused" | "completed", opts: { stopUnfollow?: boolean } = {}) =>
    api<EngageCampaign>(`/api/engage/campaigns/${enc(id)}/status`, { method: "POST", body: { status, ...(opts.stopUnfollow ? { stopUnfollow: true } : {}) } }),
  duplicate: (id: ID) => api<EngageCampaign>(`/api/engage/campaigns/${enc(id)}/duplicate`, { method: "POST", body: {} }),
  stats: (id: ID) => api<EngageStats>(`/api/engage/campaigns/${enc(id)}/stats`),
  targets: (id: ID, q: { status?: EngageTargetStatus | ""; q?: string; cursor?: string | null; limit?: number }) =>
    api<Page<EngageTarget>>(`/api/engage/campaigns/${enc(id)}/targets`, { query: { status: q.status, q: q.q, cursor: q.cursor, limit: q.limit } }),
  overview: (igAccountId: ID) => api<EngageOverview>("/api/engage/overview", { query: { igAccountId } }),
};

/** Query keys — first segment is the feature id so `["engage"]` invalidates everything. */
export const engageKeys = {
  all: ["engage"] as const,
  list: (igAccountId: ID) => ["engage", "campaigns", igAccountId] as const,
  overview: (igAccountId: ID) => ["engage", "overview", igAccountId] as const,
  campaign: (id: ID) => ["engage", "campaign", id] as const,
  targets: (id: ID, status: string, q: string) => ["engage", "targets", id, status, q] as const,
};
